/**
 * React Query mutation hook for reverting a config version.
 *
 * Drives the two-step Revert flow on the history table: first called with
 * `dryRun: true` to preview the diff inside the confirmation modal, then with
 * `dryRun: false` on confirm to apply the revert. Only a real revert
 * (`dryRun === false`) invalidates the cached lists — a dry run writes nothing,
 * so freshening the tables would be wasteful.
 *
 * A real revert restores the target version's snapshot (which writes a new
 * version row) and captures a `pre_restore` snapshot, so both the history list
 * and the snapshot list are invalidated.
 */

import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import {
  configVersioningService,
  type RevertResult,
} from '../../api/configVersioningService';
import { configVersioningKeys } from './queryKeys';

export interface RevertVersionVars {
  versionId: string;
  dryRun: boolean;
}

export function useRevertVersion(): UseMutationResult<
  RevertResult,
  Error,
  RevertVersionVars
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ versionId, dryRun }: RevertVersionVars) =>
      configVersioningService.revertVersion(versionId, dryRun),
    onSuccess: (_data, variables) => {
      if (variables.dryRun) return;
      void queryClient.invalidateQueries({
        queryKey: configVersioningKeys.historyList(),
      });
      void queryClient.invalidateQueries({
        queryKey: configVersioningKeys.snapshotList(),
      });
      void queryClient.invalidateQueries({
        queryKey: configVersioningKeys.version(variables.versionId),
      });
    },
  });
}
